/** Humanize a tool name into a user-friendly action label. */
export function humanizeToolName(name: string): string {
  const MAP: Record<string, string> = {
    // RevenueCat — specific chart presets
    fetch_revenuecat_data: 'RevenueCat',
    // Ad platforms
    fetch_meta_ads: 'Meta Ads',
    save_company_research: 'Company research',
    fetch_tiktok_ads: 'TikTok Ads',
    fetch_google_ads: 'Google Ads',
    fetch_ga4_data: 'Google Analytics',
    // Other platforms
    fetch_shopify_data: 'Shopify',
    fetch_stripe_data: 'Stripe',
    fetch_klaviyo_data: 'Klaviyo',
    fetch_amplitude_data: 'Amplitude',
    fetch_mixpanel_data: 'Mixpanel',
    fetch_adjust_data: 'Adjust',
    fetch_braze_data: 'Braze',
    fetch_chargebee_data: 'Chargebee',
    fetch_hubspot_data: 'HubSpot',
    fetch_segment_data: 'Segment',
    fetch_salesforce_data: 'Salesforce',
    fetch_amazon_ads: 'Amazon Ads',
    fetch_google_play_data: 'Google Play',
    fetch_app_store_data: 'App Store Connect',
    fetch_linkedin_ads: 'LinkedIn Ads',
    fetch_pinterest_ads: 'Pinterest Ads',
    fetch_snapchat_ads: 'Snapchat Ads',
    fetch_semrush_data: 'Semrush',
    analyze_revenuecat_csv: 'RevenueCat CSV',
    // Utility
    parse_uploaded_file: 'Parsing uploaded file',
    query_bigquery: 'Querying database',
    // Analysis
    cross_platform_reconciliation: 'Reconciling cross-platform data',
    analyze_cohorts: 'Analyzing subscriber cohorts',
    calculate_unit_economics: 'Calculating unit economics',
    model_scenarios: 'Modeling growth scenarios',
    fetch_industry_data: 'Fetching industry benchmarks',
    calculate_health_score: 'Computing business health score',
    generate_swot: 'Generating SWOT analysis',
    grade_business_areas: 'Grading business areas (AARRR)',
    analyze_seo: 'Analyzing SEO performance',
    analyze_aeo: 'Analyzing AI engine optimization',
    calculate_market_sizing: 'Calculating market size (TAM/SAM/SOM)',
    analyze_competitors: 'Analyzing competitive landscape',
    assess_macro_risks: 'Assessing macro & market risks',
    calculate_dashboard_kpis: 'Calculating dashboard KPIs',
    // Reports
    generate_marketing_report: 'Compiling final report',
    generate_pdf_report: 'Generating PDF report',
    generate_pptx_report: 'Generating PowerPoint deck',
    prepare_report_data: 'Loading audit data for report',
    // V2 analysis tools
    load_analysis_data: 'Loading data inventory',
    save_analysis: 'Saving analysis results',
    load_raw_data: 'Loading detailed data',
    compute_health_score: 'Computing business health score',
    assemble_dashboard_kpis: 'Assembling dashboard KPIs',
    // Other
    get_questionnaire_answers: 'Loading questionnaire responses',
    delegate_to_agent: 'Specialized analysis',
    ingest_uploaded_files: 'Ingesting uploaded data',
  };
  return MAP[name] || name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Humanize an agent profile / delegate target name. */
function humanizeAgentName(agent: string): string {
  const MAP: Record<string, string> = {
    'revenue-growth-agent': 'revenue & growth',
    'subscriber-health-agent': 'subscriber health',
    'funnel-conversion-agent': 'funnel & conversion',
    'unit-economics-agent': 'unit economics',
    'cross-platform-agent': 'cross-platform reconciliation',
    'strategy-agent': 'strategy',
    'marketing-agent': 'marketing',
  };
  return MAP[agent] || agent.replace(/-agent$/, '').replace(/[-_]/g, ' ');
}

/** Format a date-range value that the LLM may pass as a string or {start,end}. */
function formatDateRange(v: unknown): string | null {
  if (!v) return null;
  if (typeof v === 'string') {
    return v.length > 60 ? null : v;
  }
  if (typeof v === 'object') {
    const o = v as Record<string, unknown>;
    const start = typeof o.start === 'string' ? o.start : typeof o.from === 'string' ? o.from : null;
    const end = typeof o.end === 'string' ? o.end : typeof o.to === 'string' ? o.to : null;
    if (start && end) return `${start} → ${end}`;
    if (start) return `from ${start}`;
    if (end) return `until ${end}`;
  }
  return null;
}

/** Build a short args summary for a tool_start feed message.
 *
 * Returns a detail fragment (without tool label) like "campaigns · 2024-01 → 2024-03",
 * or ``null`` if there's nothing worth showing. Caller composes the full line.
 */
export function humanizeToolArgs(
  name: string,
  input: Record<string, unknown> | undefined | null,
): string | null {
  if (!input || typeof input !== 'object') return null;
  const parts: string[] = [];

  // Data fetcher family — metric / data_type + date_range
  if (name.startsWith('fetch_')) {
    const metric =
      (typeof input.metric === 'string' && input.metric) ||
      (typeof input.data_type === 'string' && input.data_type) ||
      (typeof input.report_type === 'string' && input.report_type) ||
      null;
    if (metric) parts.push(humanizeDataType(metric));
    const dr = formatDateRange(input.date_range ?? input.dateRange ?? input.period);
    if (dr) parts.push(dr);
    return parts.length ? parts.join(' · ') : null;
  }

  // Delegate → shows which specialist agent is being invoked
  if (name === 'delegate_to_agent') {
    const agent = typeof input.agent === 'string' ? humanizeAgentName(input.agent) : null;
    if (agent) return `→ ${agent}`;
    const task = typeof input.task === 'string' ? input.task : null;
    if (task) return task.length > 60 ? task.slice(0, 57) + '...' : task;
    return null;
  }

  // V2 analysis tools — aspect / source / data_type
  if (name === 'save_analysis') {
    const aspect = typeof input.aspect === 'string' ? input.aspect.replace(/_/g, ' ') : null;
    const title = typeof input.title === 'string' ? input.title : null;
    if (aspect) return aspect;
    if (title) return title.length > 60 ? title.slice(0, 57) + '...' : title;
    return null;
  }
  if (name === 'load_raw_data') {
    const source = typeof input.source === 'string' ? humanizeToolName(`fetch_${input.source}_data`) || input.source : null;
    const dataType = typeof input.data_type === 'string' ? humanizeDataType(input.data_type) : null;
    if (source && dataType) return `${source} · ${dataType}`;
    if (source) return source;
    if (dataType) return dataType;
    return null;
  }
  if (name === 'load_analysis_data') {
    // No useful args — tool loads the whole inventory
    return null;
  }

  // BigQuery — show a short query snippet
  if (name === 'query_bigquery') {
    const q = typeof input.query === 'string' ? input.query : null;
    if (q) {
      const one = q.replace(/\s+/g, ' ').trim();
      return one.length > 60 ? one.slice(0, 57) + '...' : one;
    }
    return null;
  }

  // File parser — show filename or id
  if (name === 'parse_uploaded_file') {
    const fname =
      (typeof input.filename === 'string' && input.filename) ||
      (typeof input.file_id === 'string' && input.file_id) ||
      null;
    return fname;
  }

  // Industry data — indicator name
  if (name === 'fetch_industry_data') {
    const ind = typeof input.indicator === 'string' ? input.indicator.replace(/_/g, ' ') : null;
    return ind;
  }

  // Analysis / strategy tools — show the most informative single string arg
  const PREFERRED_KEYS = ['calculation', 'area', 'scope', 'type', 'format', 'target', 'topic'];
  for (const key of PREFERRED_KEYS) {
    const v = input[key];
    if (typeof v === 'string' && v.length > 0 && v.length <= 80) {
      return v.replace(/_/g, ' ');
    }
  }

  // Generic fallback — first primitive string arg
  for (const [k, v] of Object.entries(input)) {
    if (typeof v === 'string' && v.length > 0 && v.length <= 80 && k !== 'chat_id' && k !== 'user_id') {
      return v.replace(/_/g, ' ');
    }
  }
  return null;
}

/** Humanize a source data_type into a readable label. */
function humanizeDataType(dataType: string): string {
  const MAP: Record<string, string> = {
    // RevenueCat chart presets
    revenue_proceeds: 'net revenue (proceeds)',
    revenue_gross: 'gross revenue',
    actives_movement: 'active subscribers movement',
    subscription_retention: 'subscription retention',
    mrr_by_platform: 'MRR by platform',
    net_revenue_by_platform: 'net revenue by platform',
    new_customers: 'new customers',
    active_customers: 'active customers',
    overview: 'overview metrics',
    plan_mix: 'plan mix analysis',
    billing_health: 'billing health',
    mrr_movement: 'MRR movement',
    project_info: 'project info',
    // Ad platform metrics
    spend_summary: 'spend summary',
    campaign_performance: 'campaign performance',
    ad_set_performance: 'ad set performance',
    audience_insights: 'audience insights',
    daily_spend: 'daily spend',
    keyword_performance: 'keyword performance',
    daily_stats: 'daily stats',
    // Analytics
    traffic_acquisition: 'traffic acquisition',
    user_acquisition: 'user acquisition',
    engagement: 'engagement metrics',
    conversion_funnel: 'conversion funnel',
    events: 'event data',
    // Commerce
    orders_summary: 'orders summary',
    revenue_metrics: 'revenue metrics',
    customer_metrics: 'customer metrics',
    subscription_summary: 'subscription summary',
    mrr_metrics: 'MRR metrics',
    churn_metrics: 'churn metrics',
  };
  return MAP[dataType] || dataType.replace(/_/g, ' ');
}

/** Classify a tool as data/analysis/strategy for counter increments. */
export type ToolCategory = 'data' | 'analysis' | 'strategy' | 'other';

export function classifyTool(name: string): ToolCategory {
  const DATA_TOOLS = new Set([
    'fetch_revenuecat_data', 'fetch_meta_ads', 'fetch_tiktok_ads',
    'fetch_google_ads', 'fetch_ga4_data', 'fetch_appsflyer_data',
    'parse_uploaded_file', 'query_bigquery', 'fetch_industry_data',
    'analyze_revenuecat_csv',
    'fetch_shopify_data', 'fetch_stripe_data', 'fetch_klaviyo_data',
    'fetch_amplitude_data', 'fetch_mixpanel_data', 'fetch_adjust_data',
    'fetch_braze_data', 'fetch_chargebee_data', 'fetch_hubspot_data',
    'fetch_segment_data', 'fetch_salesforce_data', 'fetch_amazon_ads',
    'fetch_google_play_data', 'fetch_app_store_data', 'fetch_linkedin_ads',
    'fetch_pinterest_ads', 'fetch_snapchat_ads', 'fetch_semrush_data',
  ]);
  const ANALYSIS_TOOLS = new Set([
    'cross_platform_reconciliation', 'analyze_cohorts',
    'calculate_unit_economics', 'model_scenarios',
    'analyze_seo', 'analyze_aeo', 'calculate_market_sizing',
    'analyze_competitors', 'assess_macro_risks',
    'load_analysis_data', 'load_raw_data', 'save_analysis',
  ]);
  const STRATEGY_TOOLS = new Set([
    'calculate_health_score', 'generate_swot', 'grade_business_areas',
    'compute_health_score', 'assemble_dashboard_kpis',
    'generate_marketing_report', 'generate_pdf_report', 'generate_pptx_report',
  ]);

  if (DATA_TOOLS.has(name)) return 'data';
  if (ANALYSIS_TOOLS.has(name)) return 'analysis';
  if (STRATEGY_TOOLS.has(name)) return 'strategy';
  return 'other';
}

/** Try to parse JSON and extract a human-readable summary. */
function extractFromJSON(name: string, raw: string): string | null {
  try {
    const data = JSON.parse(raw);

    // ── Uploaded-file ingestion provenance (pre-run, from services/ingestion) ──
    if (name === 'ingest_uploaded_files' && data.status === 'ingested') {
      const parts: string[] = [];
      // `shopify_metrics` is the internal data-model key (source="shopify");
      // the user-facing label stays platform-neutral since the same schema can
      // come from any e-commerce store export, not only a live Shopify account.
      const shopify = (data.shopify_metrics || {}) as Record<string, number>;
      const shopifyBits = Object.entries(shopify).map(([m, n]) => `${m} (${n})`);
      if (shopifyBits.length) parts.push(`E-commerce store data → ${shopifyBits.join(', ')}`);
      const generic = (data.generic_sources || []) as string[];
      if (generic.length) parts.push(`data files → ${generic.join(', ')}`);
      if (data.attached_files) parts.push(`${data.attached_files} file(s) attached for AI reading`);
      return parts.length ? `Uploaded data ingested: ${parts.join('; ')}` : null;
    }

    // Onboarding profile pass. MUST come before the generic "collected"
    // branch below: that one reports a row count, and a profile has no rows —
    // it reported every profile as "0 data points", which read as a failure
    // for a call that had actually collected the business identity.
    if (data.data_type === 'business_profile') {
      if (data.cached) return 'Profile already collected';
      const sections = Array.isArray(data.sections) ? data.sections : [];
      const parts: string[] = [];
      // The company we found is the most useful thing to show — it's also
      // what the web-research step searches for next.
      const identity = data.identity as { company_name?: string; website?: string } | undefined;
      const who = identity?.company_name || identity?.website;
      if (who) parts.push(String(who));
      if (sections.length > 0) parts.push(sections.join(', ').replace(/_/g, ' '));
      const missing = Array.isArray(data.unavailable) ? data.unavailable.length : 0;
      if (missing > 0) parts.push(`${missing} unavailable`);
      return parts.length > 0 ? `Profile: ${parts.join(' · ')}` : 'Profile collected';
    }
    if (name === 'save_company_research') {
      const found: string[] = [];
      if (data.competitors > 0) found.push(`${data.competitors} competitors`);
      if (data.sources > 0) found.push(`${data.sources} sources`);
      return found.length > 0 ? `Researched: ${found.join(', ')}` : 'Research saved';
    }

    // ── Source data collection summary (new format from all fetcher tools) ──
    if (data.status === 'collected' && data.source && data.data_type) {
      const source = humanizeToolName(name) || data.source;
      const dataType = humanizeDataType(data.data_type);
      const rows = data.rows || data.total_rows || data.total_campaigns || data.campaigns_with_data || 0;
      const dateRange = data.date_range ? ` (${data.date_range})` : '';
      return `${source}: collected ${dataType} — ${rows} data point${rows !== 1 ? 's' : ''}${dateRange}`;
    }

    // RevenueCat overview
    if (name === 'fetch_revenuecat_data' && data.metrics) {
      const items = data.metrics as Array<{ name: string; value: number; unit: string }>;
      const highlights = items
        .filter((m) => ['MRR', 'Revenue', 'Active Subscriptions', 'Active Trials'].includes(m.name))
        .map((m) => `${m.name}: ${m.unit === '$' ? '$' : ''}${m.value.toLocaleString()}`)
        .join(' | ');
      return highlights || `${items.length} metrics loaded`;
    }

    // RevenueCat chart data
    if (name === 'fetch_revenuecat_data' && data.display_name) {
      const summary = data.summary || {};
      const avg = summary.average || {};
      const total = summary.total || {};
      const parts: string[] = [];
      for (const [k, v] of Object.entries(total)) {
        parts.push(`${k}: ${typeof v === 'number' ? (data.yaxis === '$' ? '$' : '') + (v as number).toLocaleString() : v}`);
      }
      if (parts.length === 0) {
        for (const [k, v] of Object.entries(avg)) {
          parts.push(`Avg ${k}: ${typeof v === 'number' ? (v as number).toLocaleString() : v}`);
        }
      }
      return `${data.display_name}: ${parts.join(', ') || 'data loaded'}`;
    }

    // RevenueCat products/entitlements list
    if (name === 'fetch_revenuecat_data' && Array.isArray(data)) {
      return `${data.length} items loaded`;
    }

    // RevenueCat MRR movement
    if (name === 'fetch_revenuecat_data' && data.metric === 'mrr_movement') {
      const t = data.totals || {};
      return `MRR Movement: New ${fmtCurrency(t.new_mrr)}, Churned ${fmtCurrency(t.churned_mrr)}, Net ${fmtCurrency(t.net_movement)}`;
    }

    // RevenueCat billing health
    if (name === 'fetch_revenuecat_data' && data.billing_issues_count !== undefined) {
      return `Billing health: ${data.billing_issues_count} issues, ${data.at_risk_revenue} at risk`;
    }

    // RevenueCat plan mix
    if (name === 'fetch_revenuecat_data' && data.plan_distribution) {
      const d = data.plan_distribution;
      return `Plan mix: ${d.annual_pct || 0}% annual, ${d.monthly_pct || 0}% monthly`;
    }

    // RevenueCat generic — try to extract any useful numbers
    if (name === 'fetch_revenuecat_data') {
      // Try summary string
      if (typeof data.summary === 'string' && data.summary.length > 10) {
        return data.summary.slice(0, 90);
      }
      // Try to find any key metric fields
      const keys = Object.keys(data);
      const numericEntries = keys.filter(k => typeof data[k] === 'number' && data[k] > 0);
      if (numericEntries.length > 0) {
        return numericEntries.slice(0, 3).map(k => `${k.replace(/_/g, ' ')}: ${data[k].toLocaleString()}`).join(', ');
      }
    }

    // Meta / TikTok / Google / GA4 — API returns JSON array of row objects
    if (name === 'fetch_meta_ads' || name === 'fetch_tiktok_ads' || name === 'fetch_google_ads') {
      const platform = name === 'fetch_meta_ads' ? 'Meta' : name === 'fetch_tiktok_ads' ? 'TikTok' : 'Google';
      // Array of campaign/ad rows
      if (Array.isArray(data) && data.length > 0) {
        const totalSpend = data.reduce((s: number, r: any) => s + (parseFloat(r.spend || r.cost || 0)), 0);
        const totalImps = data.reduce((s: number, r: any) => s + (parseInt(r.impressions || 0)), 0);
        const totalClicks = data.reduce((s: number, r: any) => s + (parseInt(r.clicks || r.inline_link_clicks || 0)), 0);
        const parts: string[] = [`${data.length} records`];
        if (totalSpend > 0) parts.push(`spend: ${fmtCurrency(totalSpend)}`);
        if (totalImps > 0) parts.push(`${(totalImps / 1000).toFixed(0)}K impressions`);
        if (totalClicks > 0) parts.push(`${totalClicks.toLocaleString()} clicks`);
        return `${platform}: ${parts.join(', ')}`;
      }
      // Dict with summary
      if (data.summary) return `${platform}: ${String(data.summary).slice(0, 80)}`;
      if (data.campaigns) return `${platform}: ${data.campaigns.length} campaigns`;
      if (data.total_spend) return `${platform}: spend ${fmtCurrency(data.total_spend)}`;
      return null;
    }

    // GA4 Analytics
    if (name === 'fetch_ga4_data') {
      if (Array.isArray(data) && data.length > 0) {
        const totalSessions = data.reduce((s: number, r: any) => s + (parseInt(r.sessions || r.screenPageViews || 0)), 0);
        return `GA4: ${data.length} rows, ${totalSessions.toLocaleString()} sessions`;
      }
      if (data.summary) return `GA4: ${String(data.summary).slice(0, 80)}`;
      if (data.total_sessions) return `GA4: ${data.total_sessions.toLocaleString()} sessions`;
    }

    // Industry data
    if (name === 'fetch_industry_data') {
      if (data.summary) return String(data.summary).slice(0, 90);
      if (data.indicator) return `${data.indicator.replace(/_/g, ' ')}: ${data.latest_value || 'loaded'}`;
    }

    // Dashboard KPIs
    if (name === 'calculate_dashboard_kpis') {
      if (data.summary) return String(data.summary).slice(0, 90);
      return 'Dashboard KPIs aggregated';
    }

    // File parser
    if (name === 'parse_uploaded_file') {
      if (data.rows) return `Parsed ${data.rows} rows, ${data.columns || '?'} columns`;
      if (data.pages) return `Parsed ${data.pages} pages`;
      return data.summary ? String(data.summary).slice(0, 90) : null;
    }

    // Deterministic strategy blocks (computed + saved in code)
    if (name === 'compute_health_score') {
      if (data.status === 'saved' && typeof data.overall_score === 'number') {
        const grades = data.grades && typeof data.grades === 'object'
          ? ` | Grades: ${Object.entries(data.grades as Record<string, string>).map(([a, g]) => `${a} ${g}`).join(', ')}`
          : '';
        return `Health score ${data.overall_score}/100${grades}`;
      }
      if (data.status === 'insufficient_data') return 'Health score: insufficient data — falling back';
      return null;
    }
    if (name === 'assemble_dashboard_kpis' && data.status === 'saved') {
      const missing = Array.isArray(data.missing) && data.missing.length
        ? ` (${data.missing.length} unavailable)` : '';
      return `Dashboard KPIs assembled${missing}`;
    }

    // Health score
    if (name === 'calculate_health_score' && data.summary) {
      const match = (data.summary as string).match(/(\d+)\/100/);
      return match ? `Overall score: ${match[1]}/100` : 'Score calculated';
    }
    if (name === 'calculate_health_score' && data.health_score) {
      return `Overall score: ${data.health_score}/100`;
    }

    // SWOT
    if (name === 'generate_swot' && data.swot) {
      const s = data.swot;
      const counts = [
        s.strengths?.length || 0, s.weaknesses?.length || 0,
        s.opportunities?.length || 0, s.threats?.length || 0,
      ];
      return `${counts[0]} strengths, ${counts[1]} weaknesses, ${counts[2]} opportunities, ${counts[3]} threats`;
    }

    // Business grading
    if (name === 'grade_business_areas' && data.grades) {
      const grades = data.grades as Array<{ area: string; grade: string }>;
      return grades.map((g) => `${g.area}: ${g.grade}`).join(', ');
    }

    // Market sizing
    if (name === 'calculate_market_sizing') {
      const tam = data.tam?.value || data.tam;
      const sam = data.sam?.value || data.sam;
      const som = data.som?.value || data.som;
      if (tam || sam || som) {
        return `TAM: $${formatBigNumber(tam)} | SAM: $${formatBigNumber(sam)} | SOM: $${formatBigNumber(som)}`;
      }
    }

    // Competitors
    if (name === 'analyze_competitors' && data.comparison) {
      const count = Array.isArray(data.comparison) ? data.comparison.length : 0;
      return `${count} competitors analyzed`;
    }

    // Cohort analysis
    if (name === 'analyze_cohorts') {
      const total = data.total_cohorts || 0;
      const valid3 = data.valid_for_m3 || 0;
      return `${total} cohorts found, ${valid3} valid for M3 comparison`;
    }

    // Reconciliation
    if (name === 'cross_platform_reconciliation') {
      const type = data.reconciliation_type || 'data';
      const discCount = Array.isArray(data.discrepancies) ? data.discrepancies.length : 0;
      const detail = discCount > 0 ? ` — ${discCount} discrepancies found` : '';
      return `${type.replace(/_/g, ' ')} reconciliation complete${detail}`;
    }

    // Unit economics
    if (name === 'calculate_unit_economics') {
      const calc = data.calculation || '';
      if (calc === 'cpa_by_channel' && Array.isArray(data.metrics)) {
        return `CPA by channel: ${data.metrics.map((m: any) => `${m.channel} ${m.cpa_formatted}`).slice(0, 3).join(', ')}`;
      }
      if (calc === 'blended_economics' && data.metrics) {
        const m = data.metrics;
        return `Blended CPA: ${fmtCurrency(m.blended_cpa)}, ROAS: ${m.blended_roas?.toFixed(1)}x, LTV:CAC ${m.blended_ltv_cac_ratio?.toFixed(1)}x`;
      }
      if (calc === 'cpa_reconstruction' && data.blended_cpa) {
        return `Reconstructed CPA: ${fmtCurrency(data.blended_cpa)} (${data.total_trials} trials × ${data.trial_conversion_rate * 100}% conv)`;
      }
      if (data.summary && typeof data.summary === 'string') {
        const firstLine = data.summary.split('\n')[0];
        return firstLine.slice(0, 80);
      }
      return `${calc.replace(/_/g, ' ')} calculated`;
    }

    // Scenario modeling
    if (name === 'model_scenarios' && data.scenarios) {
      const count = Array.isArray(data.scenarios) ? data.scenarios.length : 0;
      return `${count} growth scenarios projected`;
    }

    // Macro risks
    if (name === 'assess_macro_risks') {
      const rs = data.risk_summary || {};
      if (rs.high_risks || rs.medium_risks || rs.tailwinds) {
        return `Risks: ${rs.high_risks || 0} high, ${rs.medium_risks || 0} medium, ${rs.tailwinds || 0} tailwinds`;
      }
      const cats = data.categories || {};
      const count = typeof cats === 'object' ? Object.keys(cats).length : 0;
      return `${count} risk categories assessed`;
    }

    // Questionnaire
    if (name === 'get_questionnaire_answers') {
      const answers = data.answers || {};
      const count = Object.keys(answers).length;
      return count > 0 ? `${count} answers loaded` : 'No answers submitted yet';
    }

    // Report generation
    if (name === 'generate_marketing_report') {
      return 'Structured audit report compiled';
    }

    // PDF/PPTX
    if (name === 'generate_pdf_report' || name === 'generate_pptx_report') {
      if (raw.includes('Download:') || raw.includes('successfully')) {
        return 'File generated successfully';
      }
    }

    return null;
  } catch {
    return null;
  }
}

function fmtCurrency(n: unknown): string {
  if (typeof n !== 'number' || !n) return '$0';
  if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

function formatBigNumber(n: unknown): string {
  if (typeof n !== 'number' || !n) return '—';
  if (n >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(0)}K`;
  return n.toLocaleString();
}

/** Summarize a tool result into a short, human-readable feed message. */
export function summarizeToolResult(name: string, result: string, isError: boolean): { msg: string; type: string } | null {
  const label = humanizeToolName(name);

  if (isError) {
    // Suppress non-critical errors (resource not found, 400s, empty data)
    const lower = result.toLowerCase();
    if (
      lower.includes('not found') ||
      lower.includes('404') ||
      lower.includes('resource not found') ||
      lower.includes('at least 2 datasets') ||
      lower.includes('required for reconciliation') ||
      lower.includes('missing required context')
    ) {
      return null; // skip — non-critical, agent continues with available data
    }
    // Extract just the key error message, not the full JSON
    const simplified = result
      .replace(/\{.*\}/s, '')  // strip JSON objects
      .replace(/Error:?\s*/i, '')
      .trim()
      .slice(0, 80);
    return { msg: `Warning: ${simplified || 'data source returned an error'}`, type: 'warn' };
  }

  // Try smart extraction from JSON
  const extracted = extractFromJSON(name, result);
  if (extracted) {
    const category = classifyTool(name);
    const type = category === 'strategy' ? 'finding' : category === 'analysis' ? 'finding' : '';
    return { msg: extracted, type };
  }

  // Fallback: show the tool completed
  const category = classifyTool(name);
  if (category === 'strategy') {
    return { msg: `${label} complete`, type: 'finding' };
  }
  return { msg: `${label} complete`, type: '' };
}

/** Clean Claude's text output for the feed — strip markdown formatting. */
export function cleanAgentText(content: string): string | null {
  let text = content.trim();
  if (text.length < 5) return null;

  // Strip markdown headers, bold, links, horizontal rules
  text = text
    .replace(/^#{1,4}\s*/gm, '')
    .replace(/\*\*/g, '')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/---+/g, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/[🔄🚀📊💰📈✅❌⚠️🔍💡🎯📋]/g, '')
    .trim();

  // Skip empty or very short after cleanup
  if (text.length < 10) return null;

  // Truncate long text
  if (text.length > 120) {
    text = text.slice(0, 117) + '...';
  }

  return text;
}

/** Format elapsed seconds as `[Xs]` / `[Xm Ys]` / `[Xh Ym]` — shared
 * between the live stream's getElapsed() and replayAnalysisEvents so
 * restored and live feed entries render with one consistent format.
 */
export function formatElapsedSeconds(totalSecs: number): string {
  const s = Math.max(0, totalSecs);
  if (s < 60) return `[${s.toFixed(1)}s]`;
  const mins = Math.floor(s / 60);
  const secs = s % 60;
  if (mins < 60) return `[${mins}m ${secs.toFixed(0)}s]`;
  const hrs = Math.floor(mins / 60);
  const remainMins = mins % 60;
  return `[${hrs}h ${remainMins}m]`;
}

/** Pull a short, cleaned excerpt from a thinking-block buffer to show in the
 * live feed during long gaps between tool calls. Trims whitespace, prefers
 * the last sentence, and caps the result at ~100 chars so the terminal
 * stays readable. Returns null if nothing meaningful is available.
 */
export function extractThinkingSnippet(buffer: string): string | null {
  if (!buffer) return null;
  // Collapse newlines + whitespace runs; Claude's thinking stream often
  // contains raw markdown with indented code-style blocks.
  const flat = buffer.replace(/\s+/g, ' ').trim();
  if (flat.length < 30) return null;

  // Take the trailing 100 chars as the candidate window. Then try to find
  // a sentence boundary in its first half so we don't start mid-word /
  // mid-sentence; if no boundary is found, fall back to the next space.
  let snippet = flat.slice(-100);
  const breakMatch = snippet.slice(0, 50).match(/[.!?]\s+[A-ZА-Я]/);
  if (breakMatch && breakMatch.index !== undefined) {
    snippet = snippet.slice(breakMatch.index + 2);
  } else {
    const firstSpace = snippet.indexOf(' ');
    if (firstSpace > 0 && firstSpace < 20) {
      snippet = snippet.slice(firstSpace + 1);
    }
  }
  snippet = snippet.replace(/^[-–—.,:;)\]}"'`\s]+/, '').trim();
  if (snippet.length < 20) return null;
  return snippet;
}

/** Estimate data points for counter based on tool result. */
export function estimateDataPoints(name: string): number {
  const ESTIMATES: Record<string, number> = {
    fetch_revenuecat_data: 8000,
    fetch_meta_ads: 5000,
    fetch_tiktok_ads: 4000,
    fetch_google_ads: 5000,
    fetch_ga4_data: 12000,
    parse_uploaded_file: 3000,
    query_bigquery: 6000,
  };
  return ESTIMATES[name] || 500;
}

/** Compute counter deltas for one tool_result event.
 *
 * Single source of truth used by the live stream (ProcessingView),
 * the replay path (replayEvents), and any future counter consumer.
 * Errors contribute nothing. Data tools read ``rows`` / ``total_rows``
 * from the parsed JSON; fall back to estimateDataPoints when neither
 * is present, so live and replay produce identical numbers for the
 * same persisted event.
 */
export function tallyToolResult(
  name: string,
  result: string,
  isError: boolean,
): { dataPoints?: number; insights?: number; recommendations?: number } {
  if (isError) return {};
  const category = classifyTool(name);
  if (category === 'data') {
    let pts = estimateDataPoints(name);
    if (result) {
      try {
        const parsed = JSON.parse(result);
        // A business profile has no rows — it's account identity, targeting
        // and catalog shape. Falling through to the per-tool ESTIMATE would
        // credit it with thousands of data points it never fetched.
        if (parsed.data_type === 'business_profile') return { dataPoints: 0 };
        if (typeof parsed.rows === 'number') pts = parsed.rows;
        else if (typeof parsed.total_rows === 'number') pts = parsed.total_rows;
      } catch { /* use estimate */ }
    }
    return { dataPoints: pts };
  }
  if (category === 'analysis') return { insights: 1 };
  if (category === 'strategy') return { recommendations: 1 };
  return {};
}
